import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, handleApiError } from '../services/api';
import { useMeeting } from '../contexts/MeetingContext';
import {
  DocumentTextIcon,
  PuzzlePieceIcon,
  TagIcon,
  LinkIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import toast from 'react-hot-toast';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const KPICard = ({ title, value, icon: Icon, loading = false }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
    <div className="flex items-center">
      <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-vodafone-100">
        <Icon className="h-6 w-6 text-vodafone-600" />
      </div>
      <div className="ml-4">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        {loading ? (
          <div className="h-8 bg-gray-200 rounded animate-pulse mt-1 w-16"></div>
        ) : (
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        )}
      </div>
    </div>
  </div>
);

// Chart.js configuration for proposals chart
const getProposalsChartConfig = (data) => {
  // Check if there are any conclusions
  const hasConclusions = data.some(item => item.conclusions && item.conclusions > 0);
  
  const datasets = [
    {
      label: 'Total Proposals',
      data: data.map(item => item.proposals),
      backgroundColor: '#e60000',
      borderColor: '#e60000',
      borderWidth: 1,
      borderRadius: 4,
    }
  ];
  
  // Only add conclusions dataset if there are any conclusions
  if (hasConclusions) {
    datasets.push({
      label: 'Conclusions',
      data: data.map(item => item.conclusions || 0),
      backgroundColor: '#666666',
      borderColor: '#666666',
      borderWidth: 1,
      borderRadius: 4,
    });
  }
  
  return {
    labels: data.map(item => item.name),
    datasets: datasets
  };
};

// Chart.js configuration for linkage chart
const getLinkageChartConfig = (data) => {
  // Check if there are any conclusions (linked proposals)
  const hasConclusions = data.some(item => item.linked && item.linked > 0);
  
  const datasets = [];
  
  // Only add "Linked to Conclusions" dataset if there are any conclusions
  if (hasConclusions) {
    datasets.push({
      label: 'Linked to Conclusions',
      data: data.map(item => item.linked),
      backgroundColor: '#22c55e',
      borderColor: '#22c55e',
      borderWidth: 1,
      borderRadius: 4,
    });
  }
  
  // Always show "Not Linked" dataset
  datasets.push({
    label: 'Not Linked',
    data: data.map(item => item.unlinked),
    backgroundColor: '#ef4444',
    borderColor: '#ef4444',
    borderWidth: 1,
    borderRadius: 4,
  });
  
  return {
    labels: data.map(item => item.name),
    datasets: datasets
  };
};

// Chart.js configuration for company rankings chart
const getCompanyChartConfig = (data) => {
  // Check if there are any conclusions
  const hasConclusions = data.some(item => item.conclusion_count && item.conclusion_count > 0);
  
  const datasets = [
    {
      label: 'Proposals Submitted',
      data: data.map(item => item.proposal_count),
      backgroundColor: 'rgba(59, 130, 246, 0.8)',
      borderColor: 'rgba(59, 130, 246, 1)',
      borderWidth: 1,
      borderRadius: 4,
    }
  ];
  
  // Only add "Conclusions Influenced" dataset if there are any conclusions
  if (hasConclusions) {
    datasets.push({
      label: 'Conclusions Influenced',
      data: data.map(item => item.conclusion_count),
      backgroundColor: 'rgba(16, 185, 129, 0.8)',
      borderColor: 'rgba(16, 185, 129, 1)',
      borderWidth: 1,
      borderRadius: 4,
    });
  }
  
  return {
    labels: data.map(item => item.display_name),
    datasets: datasets
  };
};

// Chart.js options for horizontal bar charts with larger text and better tooltips
// Helper function to wrap long text into multiple lines
const wrapText = (text, maxWidth = 50) => {
  if (text.length <= maxWidth) return [text];
  
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';
  
  words.forEach(word => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  });
  
  if (currentLine) lines.push(currentLine);
  return lines;
};

const getChartOptions = (navigate, subtopicsData = []) => ({
  indexAxis: 'y',
  responsive: true,
  maintainAspectRatio: false,
  onClick: (event, elements) => {
    if (elements.length > 0 && navigate) {
      const index = elements[0].index;
      const subtopic = subtopicsData[index];
      if (subtopic && subtopic.name) {
        // Navigate to subtopic browser with the selected subtopic
        navigate('/subtopics', { state: { selectedSubtopic: subtopic.name } });
      }
    }
  },
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        usePointStyle: true,
        padding: 20,
        font: {
          size: 14,
        }
      }
    },
    tooltip: {
      enabled: true,
      mode: 'nearest',
      intersect: false,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: 'white',
      bodyColor: 'white',
      borderColor: 'rgba(255, 255, 255, 0.2)',
      borderWidth: 1,
      cornerRadius: 6,
      padding: 12,
      titleFont: {
        size: 14,
        weight: 'bold',
      },
      bodyFont: {
        size: 13,
      },
      callbacks: {
        title: function(context) {
          // Show full subtopic name in tooltip title
          return context[0].label;
        },
        label: function(context) {
          // Show value with dataset label
          return `${context.dataset.label}: ${context.parsed.x}`;
        }
      }
    },
    datalabels: {
      display: true,
      anchor: 'end',
      align: 'right',
      offset: 4,
      color: 'white',
      font: {
        size: 12,
        weight: 'bold',
      },
      formatter: function(value, context) {
        // Only show label if value > 0
        return value > 0 ? value : '';
      },
      backgroundColor: function(context) {
        // Use a semi-transparent version of the bar color
        return context.dataset.backgroundColor;
      },
      borderRadius: 3,
      padding: {
        top: 2,
        bottom: 2,
        left: 6,
        right: 6
      }
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      grid: {
        display: true,
        color: 'rgba(0, 0, 0, 0.1)',
      },
      ticks: {
        precision: 0,
        font: {
          size: 12,
        }
      }
    },
    y: {
      grid: {
        display: false,
      },
      ticks: {
        maxTicksLimit: 50,
        font: {
          size: 13,
        },
        maxRotation: 0,
        minRotation: 0,
        // Wrap long labels into multiple lines
        callback: function(value, index, ticks) {
          const label = this.getLabelForValue(value);
          return wrapText(label, 50);
        }
      }
    }
  },
  layout: {
    padding: {
      top: 20,
      bottom: 20,
      left: 20,
      right: 100,
    }
  }
});

const Stats = () => {
  const navigate = useNavigate();
  const { selectedMeeting, selectedAgenda } = useMeeting();
  const [dashboardData, setDashboardData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load dashboard data
  useEffect(() => {
    const loadData = async () => {
      if (!selectedMeeting || !selectedAgenda) return;
      
      try {
        setLoading(true);
        
        // Load dashboard stats, chart data, and company analytics in parallel
        const [dashboardRes, chartRes, companyRes] = await Promise.all([
          apiService.getDashboardData(selectedMeeting, selectedAgenda),
          apiService.getDashboardCharts(selectedMeeting, selectedAgenda),
          apiService.getCompanyAnalytics(selectedMeeting, selectedAgenda)
        ]);
        
        setDashboardData(dashboardRes.data);
        setChartData(chartRes.data);
        setCompanyData(companyRes.data);
        
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [selectedMeeting, selectedAgenda]);

  // Get chart data from API response
  const proposalsChartData = chartData?.proposals_per_subtopic || [];
  const conclusionLinkageData = chartData?.conclusion_linkage || [];
  const companyRankings = companyData?.company_rankings || []; // Show all companies

  // Debug logging
  useEffect(() => {
    if (chartData) {
      console.log('Chart data received:', chartData);
      console.log('Proposals chart data:', proposalsChartData);
      console.log('First proposal item:', proposalsChartData[0]);
    }
  }, [chartData, proposalsChartData]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-32"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-200 rounded"></div>
          ))}
        </div>
        <div className="space-y-8">
          <div className="h-96 bg-gray-200 rounded"></div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
        
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Proposal Analytics</h1>
          <p className="mt-2 text-lg text-gray-600">
            Analysis of subtopic activity, contribution trends, and linkage to meeting conclusions for Agenda {selectedAgenda} in {selectedMeeting} meeting.
          </p>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <KPICard
            title="Documents Reviewed"
            value={dashboardData?.total_documents || 0}
            icon={DocumentTextIcon}
            loading={loading}
          />
          <KPICard
            title="Proposals Extracted"
            value={dashboardData?.total_proposals || 0}
            icon={ClipboardDocumentListIcon}
            loading={loading}
          />
          <KPICard
            title="Technical Subtopics"
            value={dashboardData?.technical_subtopics || 0}
            icon={TagIcon}
            loading={loading}
          />
          <KPICard
            title="Cross-Reference Links"
            value={dashboardData?.cross_reference_links || 0}
            icon={LinkIcon}
            loading={loading}
          />
          <KPICard
            title="Conclusions Found"
            value={dashboardData?.conclusions_found || 0}
            icon={CheckCircleIcon}
            loading={loading}
          />
        </div>

        {/* Charts Section - Stacked Vertically */}
        <div className="space-y-8">
          
          {/* Proposals per Subtopic Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Proposals per Subtopic ({proposalsChartData.length} subtopics)
            </h3>
            {proposalsChartData && proposalsChartData.length > 0 ? (
              <div style={{ height: '850px', cursor: 'pointer' }}>
                <Bar
                  data={getProposalsChartConfig(proposalsChartData)}
                  options={getChartOptions(navigate, proposalsChartData)}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  <p>No proposal data available</p>
                  <p className="text-sm">Charts will populate when data is loaded</p>
                </div>
              </div>
            )}
          </div>

          {/* Subtopic → Conclusion Linkage Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Proposal-Conclusion Linkage ({conclusionLinkageData.length} subtopics)
            </h3>
            {conclusionLinkageData && conclusionLinkageData.length > 0 ? (
              <div style={{ height: '850px', cursor: 'pointer' }}>
                <Bar
                  data={getLinkageChartConfig(conclusionLinkageData)}
                  options={getChartOptions(navigate, conclusionLinkageData)}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  <p>No linkage data available</p>
                  <p className="text-sm">Charts will populate when data is loaded</p>
                </div>
              </div>
            )}
          </div>

          {/* Company Contribution Rankings Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Top Contributing Companies ({companyRankings.length} companies)
              </h3>
              {companyData && (
                <div className="text-sm text-gray-600">
                  Total: {companyData.total_companies} companies, {companyData.total_proposals} proposals
                </div>
              )}
            </div>
            {companyRankings && companyRankings.length > 0 ? (
              <div style={{ height: '850px' }}>
                <Bar
                  data={getCompanyChartConfig(companyRankings)}
                  options={{
                    ...getChartOptions(null, []),
                    plugins: {
                      ...getChartOptions(null, []).plugins,
                      tooltip: {
                        ...getChartOptions(null, []).plugins.tooltip,
                        callbacks: {
                          ...getChartOptions(null, []).plugins.tooltip.callbacks,
                          afterBody: (context) => {
                            const index = context[0].dataIndex;
                            const company = companyRankings[index];
                            return [
                              `Impact Score: ${company.impact_score}`,
                              `Subtopics Involved: ${company.subtopics_involved}`,
                              `Total Linkages: ${company.total_linkages}`
                            ];
                          }
                        }
                      }
                    },
                    scales: {
                      ...getChartOptions(null, []).scales,
                      y: {
                        ...getChartOptions(null, []).scales.y,
                        ticks: {
                          ...getChartOptions(null, []).scales.y.ticks,
                          maxTicksLimit: companyRankings.length,
                          autoSkip: false,
                        }
                      }
                    }
                  }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  <p>No company data available</p>
                  <p className="text-sm">Charts will populate when data is loaded</p>
                </div>
              </div>
            )}
          </div>
        </div>
    </div>
  );
};

export default Stats; 